/**
 * What the stores owe this job — and what that costs it.
 *
 * The work order could always say it was waiting for parts. What it could
 * never say is which part, whether anybody had ordered it, when it lands, or
 * that the three weeks it spent waiting were about to be counted against the
 * maintenance team's service level.
 *
 * So this card carries four things: the parts, the timeline they have
 * travelled, the hold that stops the clock, and the message that goes to
 * whoever is doing the work when they arrive.
 *
 * It renders nothing at all when the stores hold nothing against the job. An
 * empty card on every work order teaches everybody to stop reading it.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { mockStores } from "../../mock/stores";
import { partsFor, partsSentence } from "../../domain/stores";
import {
  heldForPartsDays,
  heldForPartsMs,
  holdAdvice,
  isPartsHold,
  messageTo,
  partsHoldNote,
  partsReadyMessage,
  partsTimeline,
} from "../../domain/partsHold";
import { slaRemaining, slaState } from "../../domain/workOrderReport";
import { isOpen, type WorkOrder, type WorkOrderEvent } from "../../domain/workOrders";
import { useTransitionWorkOrder } from "../../api/queries";
import { dueText } from "../stores/StoresPage";
import { useStoresRead } from "../stores/useStores";
import { Button, Card, Table } from "../../design/components";
import { t, tn } from "../../i18n";
import s from "../stores/Stores.module.css";

export function WorkOrderParts({
  wo,
  history,
}: {
  wo: WorkOrder;
  history: readonly WorkOrderEvent[];
}) {
  const transition = useTransitionWorkOrder(wo.id);
  const [copied, setCopied] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const now = new Date();

  /* the platform's register where it holds one, else the note the desk keeps */
  const held = useStoresRead();
  const register = held.register ?? mockStores();
  const parts = useMemo(() => partsFor(wo.number, register, now), [wo.number, register]);
  const steps = useMemo(() => partsTimeline(wo.number, register, now), [wo.number, register]);

  if (!held.settled || parts.lines.length === 0) return null;

  const heldMs = heldForPartsMs(history, wo.status, now);
  const heldDays = heldForPartsDays(history, wo.status, now);
  const heldNow = wo.status === "on_hold" && history.some((e) => isPartsHold(e));
  /* what the platform says this job may do next, not what the table guesses */
  const advice = holdAdvice(parts, wo.status, heldNow, wo.allowedTransitions);
  const storeName = (code: string) => register.locations.find((l) => l.code === code)?.name ?? code;
  const message = partsReadyMessage(wo, register, now, storeName);

  const hold = () => {
    setRefused(null);
    transition.mutate(
      { to: "on_hold", onHoldReason: partsHoldNote(parts) },
      { onError: (e) => setRefused(e instanceof Error ? e.message : String(e)) },
    );
  };
  const release = () => {
    setRefused(null);
    transition.mutate(
      { to: "in_progress", note: t("Parts are in. Work restarts.") },
      { onError: (e) => setRefused(e instanceof Error ? e.message : String(e)) },
    );
  };

  return (
    <Card
      collapsible
      collapseKey="Parts from the stores"
      title="Parts from the stores"
      sub="Parts reserved for this job, and where they are"
    >
      <p className={parts.blocked ? s.refused : s.note} data-testid="wo-parts-sentence">
        {partsSentence(parts, now)}
      </p>

      {/* what the wait costs, said plainly, whichever way it falls */}
      {wo.slaHours != null && (
        <p className={s.meta} data-testid="wo-parts-sla">
          {/*
            * Three states, because the moment a hold starts the elapsed time
            * is nothing — and a card that then said "no time has been taken
            * off" would read as though the hold had not worked.
            */}
          {heldNow
            ? t(
                "This job is held for parts, so the wait is off its service level from now on{sofar}. It reads {state} — {remaining}.",
                {
                  sofar: heldDays > 0 ? t(" — {days} so far", { days: tn(heldDays, "{n} day", "{n} days") }) : "",
                  state: t(slaState(wo, now, heldMs)),
                  remaining: slaRemaining(wo, now, heldMs) ?? "",
                },
              )
            : heldMs > 0
              ? t(
                  "{days} of this job's life were spent waiting for a part. That time is off its service level, which now reads {state} — {remaining}.",
                  {
                    days: tn(heldDays, "{n} day", "{n} days"),
                    state: t(slaState(wo, now, heldMs)),
                    remaining: slaRemaining(wo, now, heldMs) ?? "",
                  },
                )
              : t(
                  "No time has been taken off this job's service level. Only a hold entered for parts does that, and a hold for anything else keeps the clock running.",
                )}
        </p>
      )}

      {/*
        * A hold stops the clock from the moment it is recorded. It does not
        * reach backwards, and a job already past its window when somebody
        * presses the button stays past it — which has to be said, or the hold
        * looks as though it did not work.
        */}
      {heldNow && slaState(wo, now, heldMs) === "breached" && (
        <p className={s.note} data-testid="wo-parts-already">
          {t(
            "This job was already past its window when the hold was recorded. A hold stops further time counting against it; it does not undo what had already passed.",
          )}
        </p>
      )}

      {/* the one action the situation calls for, and why */}
      {advice.action === "cannot" && (
        <div className={s.holdBox} data-testid="wo-parts-advice">
          <p className={s.jobWhy}>{advice.why}</p>
        </div>
      )}
      {(advice.action === "hold" || advice.action === "release") && isOpen(wo.status) && (
        <div className={s.holdBox} data-testid="wo-parts-advice">
          <p className={s.jobWhy}>{advice.why}</p>
          <Button
            variant="primary"
            disabled={transition.isPending}
            data-testid={advice.action === "hold" ? "wo-parts-hold" : "wo-parts-release"}
            onClick={advice.action === "hold" ? hold : release}
          >
            {advice.action === "hold" ? t("Put the job on hold for parts") : t("Take it off hold — the parts are in")}
          </Button>
        </div>
      )}
      {refused && (
        <p className={s.refused} role="alert" data-testid="wo-parts-refused">
          {refused}
        </p>
      )}

      <Table>
        <thead>
          <tr>
            <th>{t("Line")}</th>
            <th className={s.num}>{t("Reserved")}</th>
            <th className={s.num}>{t("Issued")}</th>
            <th className={s.num}>{t("On the shelf")}</th>
            <th>{t("Expected")}</th>
          </tr>
        </thead>
        <tbody data-testid="wo-parts">
          {parts.lines.map((l) => (
            <tr key={l.reservation.id}>
              <td>
                {l.item ? (
                  <Link to={`/stores/${encodeURIComponent(l.reservation.sku)}`}>{l.item.name}</Link>
                ) : (
                  l.reservation.sku
                )}
                <div className={s.sku}>{l.reservation.sku}</div>
              </td>
              <td className={s.num}>{l.reservation.qty}</td>
              <td className={s.num}>{l.reservation.issued}</td>
              <td className={`${s.num} ${l.outstanding > l.availableNow ? s.bad : ""}`}>{l.availableNow}</td>
              <td>
                {l.outstanding === 0 ? (
                  <span className={s.good}>{t("issued")}</span>
                ) : l.availableNow >= l.outstanding ? (
                  <span className={s.good}>{t("on the shelf, waiting to be collected")}</span>
                ) : l.expectedAt ? (
                  dueText(l.expectedAt)
                ) : (
                  <span className={s.bad}>{t("nothing on order")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* where the parts have got to, from the register's own records */}
      <h3 className={s.groupName}>{t("How the parts got here")}</h3>
      <ol className={s.timeline} data-testid="wo-parts-timeline">
        {steps.map((step) => (
          <li key={step.key} className={s.step} data-state={step.state}>
            <span className={s.when}>{step.at ? step.at.slice(0, 10) : "—"}</span>
            <span>
              {step.what}
              {step.detail && <div className={s.why}>{step.detail}</div>}
            </span>
          </li>
        ))}
      </ol>

      {/* and the message to whoever is doing the work */}
      {parts.short === 0 && parts.lines.some((l) => l.outstanding > 0) && (
        <div className={s.holdBox} data-testid="wo-parts-tell">
          <p className={s.jobWhy}>
            {t("The parts are on the shelf. {who} has not been told yet.", { who: messageTo(wo) })}
          </p>
          <pre className={s.message}>{message}</pre>
          <div className={s.actions}>
            <Button
              data-testid="wo-parts-copy"
              onClick={() => {
                void navigator.clipboard?.writeText(message).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? t("Copied") : t("Copy the message")}
            </Button>
          </div>
          <p className={s.meta}>
            {t(
              "The platform sends nothing: it has no outbound channel. Copy this into whatever the airport already uses, and the job's own record shows the parts were ready from the moment they were booked in.",
            )}
          </p>
        </div>
      )}
    </Card>
  );
}
